import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  EligibilityConfig,
  EligibilityQuestion,
  EligibilityQuestionType,
  IncomeLimitArea,
  LimitThreshold,
} from '@bybeam/platform-types';
import {
  GenericFormData,
  SimpleValidationFieldConfig,
  requiredField,
} from '@platform-ui-common/hooks/useForm/index.js';
import { AMI_FIELDS } from './constants.js';

const getAMIKeys = (key: string) => AMI_FIELDS.map((field) => [key, field].join('.'));

const getQuestionDefaults = (question: EligibilityQuestion): [string, string | undefined][] => {
  switch (question.type) {
    case EligibilityQuestionType.Content:
      return undefined;
    case EligibilityQuestionType.AMIComponent:
      return getAMIKeys(question.key).map((field) => [field, '']);
    case EligibilityQuestionType.Dropdown:
    case EligibilityQuestionType.TextInput:
    case EligibilityQuestionType.YesOrNo:
      return [[question.key, '']];
    default:
      return assertExhaustive(question);
  }
};

const getQuestionValidation = (
  question: EligibilityQuestion,
): [string, SimpleValidationFieldConfig<GenericFormData>][] => {
  switch (question.type) {
    case EligibilityQuestionType.Content:
      return undefined;
    case EligibilityQuestionType.AMIComponent:
      return getAMIKeys(question.key).map((field) => [field, requiredField()]);
    case EligibilityQuestionType.Dropdown:
    case EligibilityQuestionType.TextInput:
    case EligibilityQuestionType.YesOrNo:
      return [[question.key, requiredField()]];
    default:
      return assertExhaustive(question);
  }
};

const checkQuestionEligibility = (
  question: EligibilityQuestion,
  formData: GenericFormData,
): boolean => {
  switch (question.type) {
    case EligibilityQuestionType.AMIComponent:
      return formData[`${question.key}.isIncomeAboveLimit`] === 'false';
    case EligibilityQuestionType.Dropdown:
      return question.props.answer.includes(formData[question.key]);
    case EligibilityQuestionType.TextInput:
    case EligibilityQuestionType.YesOrNo:
      return question.props.answer && question.props.answer === formData[question.key];
    default:
      return true;
  }
};

export function getConfigFormInit(config: EligibilityConfig): {
  defaults: GenericFormData;
  validation: Record<string, SimpleValidationFieldConfig<GenericFormData>>;
} {
  if (!config || !config?.questions) return { defaults: {}, validation: {} };
  const defaults = Object.fromEntries(
    config?.questions.flatMap(getQuestionDefaults).filter(Boolean),
  );
  const validation = Object.fromEntries(
    config?.questions.flatMap(getQuestionValidation).filter(Boolean),
  );
  return { defaults, validation };
}

export function checkEligibility(config: EligibilityConfig, formData: GenericFormData): boolean {
  return config?.questions.every((question) => checkQuestionEligibility(question, formData));
}

export const getMaxIncome = (
  area: Partial<IncomeLimitArea>,
  householdSize: number,
  limit = LimitThreshold.Low,
): number =>
  area.incomeLimit.limits[limit].find((limit) => limit.householdSize === householdSize)
    ?.incomeLimit;
